import { useCallback, useEffect, useRef, useState } from "react";
import {
  trackPageExit,
  trackScroll,
  trackSectionEnd,
  trackSectionStart,
  trackTimeSpendExit,
} from "./mixpanel";

export const useScrollTracking = (): void => {
  const scrollStartTime = useRef<number>(performance.now());
  const lastScrollPosition = useRef<number>(0);
  const scrollCompletionTime = useRef<number | null>(null);
  const sectionsReached = useRef<string[]>([]);
  const lastInteraction = useRef<string>("None");
  const scrollSpeeds = useRef<Record<string, number>>({});
  const scrollData = useRef<any>({});
  const isScrollDataSent = useRef<boolean>(false);

  const getScrollPercentage = (): number => {
    const scrollTop = window.scrollY + window.innerHeight;
    const pageHeight = document.documentElement.scrollHeight;
    return Math.min(Math.round((scrollTop / pageHeight) * 100), 100);
  };

  const calculateScrollSpeed = (currentPosition: number): number => {
    const now = performance.now();
    const distance = Math.abs(currentPosition - lastScrollPosition.current);
    const timeElapsed = (now - scrollStartTime.current) / 1000;
    scrollStartTime.current = now;
    lastScrollPosition.current = currentPosition;
    return timeElapsed > 0 ? distance / timeElapsed : 0;
  };

  const detectSectionReached = useCallback((): void => {
    const allSections = document.querySelectorAll("[data-section]");
    allSections.forEach((section: Element) => {
      const sectionName = section.getAttribute("data-section") || "Unknown";
      const sectionTop = section.getBoundingClientRect().top + window.scrollY;
      if (
        !sectionsReached.current.includes(sectionName) &&
        window.scrollY + window.innerHeight >= sectionTop
      ) {
        sectionsReached.current.push(sectionName);
        const sectionSpeed = calculateScrollSpeed(window.scrollY);
        scrollSpeeds.current[sectionName] = sectionSpeed;
      }
    });
  }, []);

  const handleScroll = useCallback((): void => {
    const scrollPercentage = getScrollPercentage();
    detectSectionReached();

    if (scrollPercentage === 100 && scrollCompletionTime.current === null) {
      scrollCompletionTime.current = Math.round(performance.now() / 1000);
    }

    scrollData.current = {
      scrollPercentage,
      scrollCompletionTime: scrollCompletionTime.current,
      scrollSpeed: calculateScrollSpeed(window.scrollY),
      scrollInteractionSpeed: { ...scrollSpeeds.current },
      contentSectionsReached: [...sectionsReached.current],
      lastInteractionBeforeScroll: lastInteraction.current,
    };

    if (scrollPercentage === 100 && !isScrollDataSent.current) {
      trackScroll({
        ...scrollData.current,
        scrollCompletionTime: scrollCompletionTime.current,
      });
      isScrollDataSent.current = true;
    }
  }, [detectSectionReached]);

  const handlePageExit = useCallback((): void => {
    if (!isScrollDataSent.current) {
      trackScroll({
        ...scrollData.current,
        scrollCompletionTime: scrollCompletionTime.current,
      });
      isScrollDataSent.current = true;
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", handleScroll);
      window.addEventListener("beforeunload", handlePageExit);

      const handleClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const interaction = target.getAttribute("data-interaction");
        if (interaction) {
          lastInteraction.current = interaction;
        }
      };
      document.addEventListener("click", handleClick);

      return () => {
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("beforeunload", handlePageExit);
        document.removeEventListener("click", handleClick);
      };
    }
  }, [handleScroll, handlePageExit]);
};

// Usage:
// Add data-section="Component Name" to the elements you want to track as content sections.
// Add data-interaction="CTA Click" or similar attributes to elements to log user interactions.

export const usePageExitTracking = (): void => {
  const lastInteraction = useRef<string>(""); // Use useRef to persist the value

  useEffect(() => {
    let exitIntent = false;
    let timeSpent = 0;
    const exitSource = "Direct"; // Default exit source (can be customized)

    // Track exit intent (mouse movement towards top of the page)
    const handleMouseMove = (event: MouseEvent) => {
      if (event.clientY < 10) {
        exitIntent = true; // Set exit intent when mouse moves towards the top of the screen
      }
    };

    // Track time spent on page
    const timer = setInterval(() => {
      timeSpent += 1;
    }, 1000); // Update every second

    // Track user interactions (click events)
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const interaction = target.getAttribute("data-interaction");
      if (interaction) {
        lastInteraction.current = interaction; // Store the interaction
      }
    };

    // Function to track exit when user is leaving the page
    const handleBeforeUnload = () => {
      const exitData = {
        exitPage: window.location.pathname,
        exitIntent: exitIntent,
        interactionBeforeExit: lastInteraction.current, // Include the last interaction
        exitTiming: new Date().toLocaleTimeString(),
        exitSource: exitSource,
        timeSpentInSession: timeSpent,
        exitPageType: "Transactional", // Adjust based on the type of page
      };

      trackPageExit(exitData); // Track the exit data in Mixpanel
    };
    if (typeof window !== "undefined") {
      // Listen for mouse movement and user clicks
      window.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("click", handleClick);

      // Listen for page unload
      window.addEventListener("beforeunload", handleBeforeUnload);

      // Clean up listeners when the component is unmounted
      return () => {
        clearInterval(timer);
        window.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("click", handleClick);
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  }, []);
};

const debounce = <T extends (...args: any[]) => void>(
  func: T,
  delay: number
): T => {
  let timer: NodeJS.Timeout | null = null;

  return ((...args: Parameters<T>) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  }) as T;
};

export const useTimeSpentTracking = (): void => {
  const pageStartTime = useRef<number>(performance.now());
  const activeTime = useRef<number>(0);
  const idleTime = useRef<number>(0);
  const lastInteractionTime = useRef<number>(performance.now());
  const interactionCount = useRef<number>(0);
  const idleDetectionTriggered = useRef<boolean>(false);

  const idleTimeout = useRef<NodeJS.Timeout | null>(null);
  const idleThreshold = 10000; // 10 seconds of inactivity
  const interactionDurations = useRef<number[]>([]);

  const calculateEngagementScore = useCallback((): number => {
    const interactionRate =
      interactionCount.current / (activeTime.current / 1000 || 1);
    return Math.round((interactionRate + activeTime.current / 1000) * 10);
  }, []);

  const handleInteraction = useCallback((): void => {
    interactionCount.current++;
    const now = performance.now();

    // Record interaction time
    if (lastInteractionTime.current) {
      const interactionDuration = (now - lastInteractionTime.current) / 1000;
      interactionDurations.current.push(interactionDuration);
    }

    lastInteractionTime.current = now;

    // Clear idle timeout on interaction
    if (idleTimeout.current) {
      clearTimeout(idleTimeout.current);
    }

    // Restart idle detection
    idleTimeout.current = setTimeout(() => {
      idleDetectionTriggered.current = true;
      const nowIdle = performance.now();
      idleTime.current += nowIdle - lastInteractionTime.current;
    }, idleThreshold);
  }, [idleThreshold]);

  const calculateMetrics = useCallback((): void => {
    const pageEndTime = performance.now();
    const totalDuration = (pageEndTime - pageStartTime.current) / 1000;
    const totalInteractions = interactionDurations.current.length;
    const avgInteractionTime =
      totalInteractions > 0
        ? interactionDurations.current.reduce((sum, val) => sum + val, 0) /
          totalInteractions
        : 0;

    const data = {
      pageDuration: Math.round(totalDuration),
      averageInteractionTime: Math.round(avgInteractionTime),
      activeTime: Math.round(activeTime.current / 1000),
      idleTime: Math.round(idleTime.current / 1000),
      interactionCount: interactionCount.current,
      idleDetectionTriggered: idleDetectionTriggered.current,
      engagementScore: calculateEngagementScore(),
    };

    // Send data to analytics
    trackTimeSpendExit(data);
  }, [calculateEngagementScore]);

  useEffect(() => {
    const interactionEvents = [
      "click",
      "scroll",
      "keydown",
      "mousemove",
      "touchstart",
    ];
    const debouncedInteraction = debounce(handleInteraction, 100);

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === "hidden") {
        calculateMetrics();
      }
    };
    if (typeof window !== "undefined") {
      // Attach interaction listeners
      interactionEvents.forEach((event) =>
        window.addEventListener(event, debouncedInteraction)
      );

      // Attach visibility change listener
      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        // Remove listeners on cleanup
        interactionEvents.forEach((event) =>
          window.removeEventListener(event, debouncedInteraction)
        );
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );

        // Clear idle timeout if active
        if (idleTimeout.current) {
          clearTimeout(idleTimeout.current);
        }

        // Send metrics on unmount
        calculateMetrics();
      };
    }
  }, [handleInteraction, calculateMetrics]);
};
// Helper function to format timestamp in "YYYY-MM-DD HH:mm:ss" format
const formatTimestamp = (timestamp: string) => {
  const date = new Date(timestamp);
  return `${date.getFullYear()}-${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")} ${date
    .getHours()
    .toString()
    .padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}:${date
    .getSeconds()
    .toString()
    .padStart(2, "0")}`;
};

// Custom hook to track session start
export const useSessionStartTracking = () => {
  const [sessionStartTime, setSessionStartTime] = useState<string>("");
  const pageViewCount = useRef<number>(1); // Initialize page navigation count

  useEffect(() => {
    const sessionStart = new Date().toISOString();
    setSessionStartTime(formatTimestamp(sessionStart));

    // Detect device type
    const deviceType = /Mobi|Android/i.test(navigator.userAgent)
      ? "Smartphone"
      : "Desktop";

    // Detect browser version
    const browserVersion = navigator.userAgent.match(/(Chrome|Firefox)\/(\d+)/);
    const browserVersionString = browserVersion
      ? `${browserVersion[1]} ${browserVersion[2]}`
      : "Unknown";

    // Detect operating system
    const os = /Mac|Win/i.test(navigator.platform)
      ? navigator.platform.includes("Win")
        ? "Windows"
        : "Mac OS"
      : "Unknown";

    // Track navigation events to count pages
    const handleNavigation = () => {
      pageViewCount.current += 1;
    };
    if (typeof window !== "undefined") {
      window.addEventListener("popstate", handleNavigation);
      window.addEventListener("pushstate", handleNavigation);

      // Track session start
      trackSectionStart({
        sessionStartTime: formatTimestamp(sessionStart),
        entryPage: window.location.pathname,
        sessionDeviceType: deviceType,
        sessionBrowserVersion: browserVersionString,
        operatingSystem: os,
      });

      return () => {
        window.removeEventListener("popstate", handleNavigation);
        window.removeEventListener("pushstate", handleNavigation);
      };
    }
  }, []); // The dependency array remains empty

  return { sessionStartTime, pageViewCount }; // Return session start data
};

// Custom hook to track session end
export const useSessionEndTracking = (
  sessionStartTime: string,
  pageViewCount: React.MutableRefObject<number>, // Pass as a parameter
  sessionEngagementScore: number
) => {
  const [sessionEndTime, setSessionEndTime] = useState<string>("");
  const [sessionDuration, setSessionDuration] = useState<number>(0);
  const [lastPageViewed, setLastPageViewed] = useState<string>("");
  const [exitInteraction, setExitInteraction] = useState<string>("");
  const lastInteraction = useRef<string>("Unknown");

  useEffect(() => {
    // Track interactions
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const interaction = target.getAttribute("data-interaction");
      if (interaction) {
        lastInteraction.current = interaction; // Store the last interaction
      }
    };
    if (typeof window !== "undefined") {
      window.addEventListener("click", handleClick);

      const handleUnload = () => {
        const sessionEnd = new Date().toISOString();
        setSessionEndTime(formatTimestamp(sessionEnd));

        const sessionStartDate = new Date(sessionStartTime);
        const sessionEndDate = new Date(sessionEnd);
        const duration = Math.floor(
          (sessionEndDate.getTime() - sessionStartDate.getTime()) / 1000
        ); // Duration in seconds
        setSessionDuration(duration);

        setLastPageViewed(window.location.pathname);
        setExitInteraction("Page Exit");

        // Track session end
        trackSectionEnd({
          sessionEndTime: formatTimestamp(sessionEnd),
          sessionDuration: duration,
          lastPageViewed: window.location.pathname,
          exitInteraction: "Page Exit",
          lastInteraction: lastInteraction.current,
          sessionEngagementScore,
          totalPagesViewed: pageViewCount.current, // Use the count
        });
      };
      if (typeof window !== "undefined") {
        // Listen for unload event
        window.addEventListener("beforeunload", handleUnload);

        return () => {
          window.removeEventListener("click", handleClick);
          window.removeEventListener("beforeunload", handleUnload);
        };
      }
    }
  }, [sessionStartTime, sessionEngagementScore, pageViewCount]);

  return { sessionEndTime, sessionDuration, lastPageViewed, exitInteraction };
};
